import { select, takeEvery, call } from 'redux-saga/effects';
import { put } from 'store/utils';
import * as api from 'utils/apiCalls';
import * as chimeActions from 'store/chime/chimeActions';
import * as audioVideoActions from './audioVideoActions';

import * as videoActions from 'store/video/videoActions';

export function* toggleAudioVideoPreviewFlowSaga(action) {
  const key = 'toggleAudioVideoPreview';
  yield put(chimeActions.initAction({ key }));
  try {
    const { audioVideo, currentVideoInputDevice, startWithAudio, startWithVideo } = yield select(
      ({ chime, audioVideo }) => ({
        audioVideo: chime.audioVideo,
        currentVideoInputDevice: chime.currentVideoInputDevice,
        startWithAudio: audioVideo.startWithAudio,
        startWithVideo: audioVideo.startWithVideo,
      })
    );
    const {
      videoDevice = null,
      // audioDevice = null,
      setStartWithVideo = false,
      setStartWithAudio = false,
    } = action.payload;

    if (setStartWithAudio) {
      yield put(audioVideoActions.startWithAudioAction(!startWithAudio));
    }
    if (setStartWithVideo) {
      if (!startWithVideo) {
        if (currentVideoInputDevice) {
          yield audioVideo.chooseVideoInputDevice(currentVideoInputDevice);
          yield audioVideo.startVideoPreviewForVideoInput(videoDevice);
        }
      } else {
        yield audioVideo.stopVideoPreviewForVideoInput(videoDevice);
      }
      yield put(audioVideoActions.startWithVideoAction(!startWithVideo));
    }

    yield put(chimeActions.successAction({ key }));
  } catch (error) {
    yield put(chimeActions.errorAction({ key, error }));
  }
}

export function* audioVideoTileDidUpdateFlowSaga(action) {
  const key = 'videoTileDidUpdate';
  yield put(audioVideoActions.initAction({ key }));
  try {
    const { tileState } = action.payload;

    if (tileState.isContent) {
      yield put(
        videoActions.setContentTileAction({
          id: tileState.boundAttendeeId,
          tileId: tileState.tileId,
        })
      );
      yield put(videoActions.startContentShareAction({ attendeeId: tileState.boundAttendeeId }));
    } else if (tileState.localTile) {
      yield put(
        videoActions.setLocalTileAction({
          attendeeId: tileState.boundAttendeeId,
          tileId: tileState.tileId,
        })
      );
    } else {
      yield put(
        videoActions.setTileAction({
          attendeeId: tileState.boundAttendeeId,
          tileId: tileState.tileId,
          isActive: tileState.active,
        })
      );
    }
    yield put(audioVideoActions.successAction({ key }));
  } catch (error) {
    yield put(audioVideoActions.errorAction({ key, error }));
    throw error;
  }
}
export function* audioVideoTileWasRemovedFlowSaga(action) {
  const key = 'videoTileWasRemoved';
  yield put(audioVideoActions.initAction({ key }));
  try {
    const { tileId } = action.payload;
    const { contentTile } = yield select(({ video }) => ({ contentTile: video.contentTile }));
    if (tileId === contentTile?.tileId) {
      yield put(videoActions.stopContentShareAction());
      yield put(videoActions.setContentTileAction({}));
    } else {
      yield put(videoActions.removeTileAction({ tileId }));
    }
    yield put(audioVideoActions.successAction({ key }));
  } catch (error) {
    yield put(audioVideoActions.errorAction({ key, error }));
    throw error;
  }
}

export function* subscribeToActiveSpeakerDetectorCallbackFlowSaga(action) {
  const key = 'subscribeToActiveSpeakerDetectorCallback';
  yield put(audioVideoActions.initAction({ key }));
  try {
    const { activeSpeakers } = action.payload;
    yield put(chimeActions.setActiveSpeakersAction(activeSpeakers));
    yield put(audioVideoActions.successAction({ key }));
  } catch (error) {
    yield put(audioVideoActions.errorAction({ key, error }));
    throw error;
  }
}

export function* realtimeSubscribeToVolumeIndicatorCallbackFlowSaga(action) {
  const key = 'realtimeSubscribeToVolumeIndicatorCallback';
  yield put(audioVideoActions.initAction({ key }));
  try {
    const { attendeeId, muted, externalUserId } = action.payload;
    const { roster } = yield select(({ chime }) => ({ roster: chime.roster }));
    if (!roster[attendeeId]) {
      const { Name, IsRecordingUser, ExternalUserId } = yield call(api.attendee, externalUserId);
      yield put(
        chimeActions.addRosterAttendeeAction({
          attendeeId,
          value: { name: Name, isRecordingUser: IsRecordingUser, externalUserId: ExternalUserId },
        })
      );
    }
    if (muted !== null) {
      yield put(
        chimeActions.updateRosterAttributeAction({ attendeeId, key: 'muted', value: muted })
      );
    }
    yield put(audioVideoActions.successAction({ key }));
  } catch (error) {
    yield put(audioVideoActions.errorAction({ key, error }));
    throw error;
  }
}

export function* realtimeSubscribeToSetCanUnmuteLocalAudioFlowSaga(action) {
  const key = 'realtimeSubscribeToSetCanUnmuteLocalAudio';
  yield put(audioVideoActions.initAction({ key }));
  try {
    const { canUnmute } = action.payload;
    const audioVideo = yield select(({ chime }) => chime.audioVideo);
    canUnmute ? audioVideo.realtimeUnmuteLocalAudio() : audioVideo.realtimeMuteLocalAudio();
    yield put(audioVideoActions.successAction({ key }));
  } catch (error) {
    yield put(audioVideoActions.errorAction({ key, error }));
    throw error;
  }
}

export function* deviceChangedFlowSaga(action) {
  const key = 'deviceChanged';
  yield put(audioVideoActions.initAction({ key }));
  try {
    const { deviceList, deviceType, freshDeviceList } = action.payload;
    const currentDevice = yield select(({ chime }) => chime?.[deviceType]);
    if (!currentDevice) return null;
    let hasCurrentDevice = false;
    const availableDevices = freshDeviceList.map(({ deviceId, label }) => {
      if (currentDevice && deviceId === currentDevice.value) hasCurrentDevice = true;
      return {
        label,
        value: deviceId,
      };
    });

    if (!hasCurrentDevice) {
      yield put(
        chimeActions.setCurrentDeviceAction({
          deviceType,
          deviceValue: availableDevices.length > 0 ? availableDevices[0].value : null,
        })
      );
    }

    yield put(
      chimeActions.setAudioDeviceListAction({ key: deviceList, devices: availableDevices })
    );
    yield put(audioVideoActions.successAction({ key }));
  } catch (error) {
    yield put(audioVideoActions.errorAction({ key, error }));
    throw error;
  }
}

export default function* chimeWatcher() {
  yield takeEvery(
    audioVideoActions.toggleAudioVideoPreviewAction().type,
    toggleAudioVideoPreviewFlowSaga
  );
  yield takeEvery(
    audioVideoActions.audioVideoTileDidUpdateAction().type,
    audioVideoTileDidUpdateFlowSaga
  );
  yield takeEvery(
    audioVideoActions.audioVideoTileWasRemovedAction().type,
    audioVideoTileWasRemovedFlowSaga
  );
  yield takeEvery(
    audioVideoActions.subscribeToActiveSpeakerDetectorCallbackAction().type,
    subscribeToActiveSpeakerDetectorCallbackFlowSaga
  );
  yield takeEvery(
    audioVideoActions.realtimeSubscribeToVolumeIndicatorCallbackAction().type,
    realtimeSubscribeToVolumeIndicatorCallbackFlowSaga
  );
  yield takeEvery(
    audioVideoActions.realtimeSubscribeToSetCanUnmuteLocalAudioAction().type,
    realtimeSubscribeToSetCanUnmuteLocalAudioFlowSaga
  );

  yield takeEvery(audioVideoActions.deviceChangedAction().type, deviceChangedFlowSaga);
}
